import { Check, Ellipsis } from 'lucide-react';
import React from 'react';
import {
    Button as AriaButton,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    type MenuItemProps as AriaMenuItemProps,
    type MenuProps as AriaMenuProps,
    Header,
    MenuSection,
    MenuTrigger,
    type PopoverProps,
    Separator,
    composeRenderProps,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import { classnames } from '@/utils/classnames.ts';
import useCopyToClipboard from '@/utils/useCopyToClipboard.ts';

import IconButton from './IconButton.tsx';
import Popover from './Popover.tsx';

// Usage: <Menu.Trigger>{trigger button}<Menu>…</Menu></Menu.Trigger>. The trigger is a react-aria Button wrapped
// in our Button/IconButton via asChild; Menu.Trigger must not add a button of its own.
export interface MenuProps<T> extends AriaMenuProps<T> {
    placement?: PopoverProps['placement'];
}

const MenuRoot = <T extends object>({ placement = 'bottom end', className, ...props }: MenuProps<T>) => (
    <Popover placement={placement} className="min-w-48">
        <AriaMenu
            {...props}
            className={composeRenderProps(className, (className) => classnames('outline-none', className))}
        />
    </Popover>
);

export interface MenuItemProps extends Omit<AriaMenuItemProps, 'children'> {
    icon?: React.ReactNode;
    // Red, for destructive actions like deleting.
    isDanger?: boolean;
    children: React.ReactNode;
}

const MenuItem = ({ icon, isDanger = false, className, children, ...props }: MenuItemProps) => (
    <AriaMenuItem
        {...props}
        textValue={props.textValue ?? (typeof children === 'string' ? children : undefined)}
        className={composeRenderProps(className, (className) =>
            classnames(
                'flex cursor-default items-center gap-2.5 rounded-lg px-3 py-2 text-base outline-none',
                'data-[focused]:bg-surface-muted data-[disabled]:opacity-50 [&_.lucide]:text-text-muted',
                isDanger && 'text-negative [&_.lucide]:text-negative',
                className
            )
        )}
    >
        {icon}
        {children}
    </AriaMenuItem>
);

interface MenuCopyItemProps {
    value: string;
    icon: React.ReactNode;
    children: string;
}

// Copies the value and stays open for a moment saying so, since a menu that closed would say nothing.
const MenuCopyItem = ({ value, icon, children }: MenuCopyItemProps) => {
    const { t } = useTranslation();
    const { isCopied, copy } = useCopyToClipboard();

    return (
        <MenuItem
            icon={isCopied ? <Check /> : icon}
            textValue={children}
            shouldCloseOnSelect={false}
            onAction={() => void copy(value)}
        >
            {isCopied ? t('common.copied') : children}
        </MenuItem>
    );
};

interface MenuSectionProps {
    // A muted line above the section's items, e.g. who a user menu is for.
    title: React.ReactNode;
    children: React.ReactNode;
}

const MenuTitledSection = ({ title, children }: MenuSectionProps) => (
    <MenuSection>
        <Header className="truncate px-3 py-1.5 text-sm text-text-muted">{title}</Header>
        {children}
    </MenuSection>
);

const MenuSeparator = () => <Separator className="my-1 border-t border-border" />;

// The usual "…" trigger for a menu of secondary actions.
interface MenuMoreButtonProps {
    'aria-label'?: string;
    // `sm` among a row's small actions, e.g. a comment's.
    size?: 'sm' | 'md';
    // `secondary` in a page header, where a ghost button next to the other actions is easy to miss.
    variant?: 'ghost' | 'secondary';
}

const MenuMoreButton = ({ 'aria-label': ariaLabel, size = 'md', variant = 'ghost' }: MenuMoreButtonProps) => {
    const { t } = useTranslation();

    return (
        <IconButton asChild size={size} variant={variant} aria-label={ariaLabel ?? t('common.moreActions')}>
            <AriaButton>
                <Ellipsis className={size === 'sm' ? 'size-3.5' : undefined} />
            </AriaButton>
        </IconButton>
    );
};

export default Object.assign(MenuRoot, {
    Trigger: MenuTrigger,
    Item: MenuItem,
    CopyItem: MenuCopyItem,
    Section: MenuTitledSection,
    Separator: MenuSeparator,
    MoreButton: MenuMoreButton,
});
